/**
 * Componente EmailOTPSetup
 * Il Diario di Bordo - SaaS
 *
 * Setup autenticazione via Email OTP.
 */

import { useState } from 'react'
import { useTwoFactor } from '../../hooks/useTwoFactor'
import { Mail, Check, Loader2, AlertCircle, Shield } from 'lucide-react'

export default function EmailOTPSetup({ userEmail, onComplete, onCancel }) {
  const [step, setStep] = useState('confirm') // confirm, done
  const [backupCodes, setBackupCodes] = useState(null)
  
  const { enableEmailOTP, loading, error, clearError } = useTwoFactor()

  const handleEnable = async () => {
    try {
      clearError()
      const data = await enableEmailOTP()
      setBackupCodes(data.backupCodes)
      setStep('done')
    } catch (err) {
      console.error('Errore abilitazione Email OTP:', err)
    }
  }

  const handleComplete = () => {
    onComplete?.({ backupCodes })
  }

  // Maschera email per privacy
  const maskedEmail = userEmail 
    ? userEmail.replace(/(.{2})(.*)(@.*)/, '$1***$3')
    : '***'

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP: Confirm
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (step === 'confirm') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 mb-4">
            <Mail className="h-6 w-6 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Autenticazione via Email
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Riceverai un codice di verifica via email ad ogni accesso
          </p>
        </div>

        {/* Info */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
          <div className="flex items-start">
            <Mail className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Email di destinazione
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {maskedEmail}
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <Shield className="h-5 w-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Come funziona
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Dopo aver inserito la password, riceverai un codice a 6 cifre via email da inserire per completare l'accesso.
              </p>
            </div>
          </div>
        </div>

        {/* Vantaggi */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Vantaggi:</p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li className="flex items-center">
              <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
              Nessuna app aggiuntiva necessaria
            </li>
            <li className="flex items-center">
              <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
              Funziona su qualsiasi dispositivo
            </li>
            <li className="flex items-center">
              <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
              Codici temporanei (10 minuti)
            </li>
          </ul>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center text-red-700 dark:text-red-400">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Pulsanti */}
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 btn-secondary"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleEnable}
            disabled={loading}
            className="flex-1 btn-primary disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Attivazione...
              </>
            ) : (
              'Attiva Email OTP'
            )}
          </button>
        </div>
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // STEP: Done
  // ═══════════════════════════════════════════════════════════════════════════
  
  if (step === 'done') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
            <Check className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Email OTP attivato!
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Riceverai un codice via email ad ogni accesso
          </p>
        </div>

        {/* Backup codes */}
        {backupCodes && backupCodes.length > 0 && (
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
              ⚠️ Codici di backup
            </h4>
            <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
              Salva questi codici in un luogo sicuro. Potrai usarli se non riesci a ricevere l'email.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, i) => (
                <code
                  key={i}
                  className="px-3 py-2 bg-white dark:bg-gray-800 rounded text-sm font-mono text-center"
                >
                  {code}
                </code>
              ))}
            </div>
            <p className="mt-3 text-xs text-amber-600 dark:text-amber-400">
              Questi codici non verranno più mostrati.
            </p>
          </div>
        )}

        <button
          type="button"
          onClick={handleComplete}
          className="w-full btn-primary"
        >
          Fatto
        </button>
      </div>
    )
  }

  return null
}
